/**
 * Sxema grafi: hisoblash tartibi, sikllar (retsikl) va tear stream tanlash.
 *
 * ALGORITM
 *   1. Tarjan SCC — kuchli bog'langan komponentlar (har retsikl halqasi bitta SCC).
 *   2. Kondensatsiya DAG bo'yicha topologik tartib (Kahn).
 *   3. Har sikl SCC ichida tear: foydalanuvchi bergan `tear` (tekshiriladi: olib
 *      tashlansa SCC asiklik bo'lishi shart) yoki avtomatik — DFS orqaga qaytuvchi
 *      yoylar (back edges). Bu optimal (minimal) kesim emas, lekin har doim to'g'ri;
 *      ko'p halqali sxemalarda `tear` ni qo'lda berish tavsiya etiladi.
 *   4. SCC ichidagi tugunlar tear'siz grafda topologik tartiblanadi.
 *
 * MANBA: Seider, Seader, Lewin, 7-bob; Tarjan (1972).
 */
import type { Flowsheet, StreamDef } from './schema';

export interface Block {
  /** tugun id lari hisoblash tartibida */
  nodes: string[];
  /** sikl bo'lsa — tear stream id lari */
  tears: string[];
}

export interface GraphPlan {
  blocks: Block[];
  /** tugun → kirish oqimlari (sxema tartibida) */
  inlets: Record<string, StreamDef[]>;
  /** tugun → chiqish oqimlari */
  outlets: Record<string, StreamDef[]>;
}

function tarjan(nodes: string[], edges: Map<string, string[]>): string[][] {
  let index = 0;
  const idx = new Map<string, number>();
  const low = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const out: string[][] = [];
  const strong = (v: string) => {
    idx.set(v, index);
    low.set(v, index);
    index++;
    stack.push(v);
    onStack.add(v);
    for (const w of edges.get(v) ?? []) {
      if (!idx.has(w)) {
        strong(w);
        low.set(v, Math.min(low.get(v)!, low.get(w)!));
      } else if (onStack.has(w)) low.set(v, Math.min(low.get(v)!, idx.get(w)!));
    }
    if (low.get(v) === idx.get(v)) {
      const comp: string[] = [];
      let w: string;
      do {
        w = stack.pop() as string;
        onStack.delete(w);
        comp.push(w);
      } while (w !== v);
      out.push(comp);
    }
  };
  for (const v of nodes) if (!idx.has(v)) strong(v);
  return out;
}

/** Kahn topologik saralash; sikl qolsa null */
function topo(nodes: string[], edges: (v: string) => string[]): string[] | null {
  const indeg = new Map<string, number>(nodes.map((n) => [n, 0]));
  for (const v of nodes) for (const w of edges(v)) if (indeg.has(w)) indeg.set(w, indeg.get(w)! + 1);
  const queue = nodes.filter((n) => indeg.get(n) === 0);
  const order: string[] = [];
  while (queue.length) {
    const v = queue.shift() as string;
    order.push(v);
    for (const w of edges(v)) {
      if (!indeg.has(w)) continue;
      indeg.set(w, indeg.get(w)! - 1);
      if (indeg.get(w) === 0) queue.push(w);
    }
  }
  return order.length === nodes.length ? order : null;
}

/** DFS orqaga qaytuvchi yoylarni tear sifatida tanlash (SCC ichida) */
function autoTears(scc: Set<string>, streams: StreamDef[], entry: string): string[] {
  const adj = new Map<string, StreamDef[]>();
  for (const s of streams) if (scc.has(s.from) && scc.has(s.to)) adj.set(s.from, [...(adj.get(s.from) ?? []), s]);
  const color = new Map<string, 0 | 1 | 2>();
  const tears: string[] = [];
  const dfs = (v: string) => {
    color.set(v, 1);
    for (const s of adj.get(v) ?? []) {
      const c = color.get(s.to) ?? 0;
      if (c === 1) tears.push(s.id);
      else if (c === 0) dfs(s.to);
    }
    color.set(v, 2);
  };
  dfs(entry);
  for (const v of scc) if (!color.has(v)) dfs(v);
  return tears;
}

export function planGraph(f: Flowsheet): GraphPlan {
  const nodeIds = f.nodes.map((n) => n.id);
  const inlets: Record<string, StreamDef[]> = {};
  const outlets: Record<string, StreamDef[]> = {};
  for (const id of nodeIds) {
    inlets[id] = [];
    outlets[id] = [];
  }
  for (const s of f.streams) {
    (outlets[s.from] as StreamDef[]).push(s);
    (inlets[s.to] as StreamDef[]).push(s);
  }
  const edges = new Map<string, string[]>(nodeIds.map((id) => [id, (outlets[id] as StreamDef[]).map((s) => s.to)]));
  const sccs = tarjan(nodeIds, edges);
  // kondensatsiya tartibi
  const compOf = new Map<string, number>();
  sccs.forEach((c, i) => c.forEach((v) => compOf.set(v, i)));
  const compIds = sccs.map((_, i) => String(i));
  const compEdges = (ci: string) => {
    const out = new Set<string>();
    for (const v of sccs[Number(ci)] as string[]) for (const w of edges.get(v) ?? []) {
      const cw = String(compOf.get(w));
      if (cw !== ci) out.add(cw);
    }
    return [...out];
  };
  const compOrder = topo(compIds, compEdges) as string[];
  const userTears = new Set(f.tear ?? []);
  const blocks: Block[] = [];
  for (const ci of compOrder) {
    const members = sccs[Number(ci)] as string[];
    const set = new Set(members);
    const internal = f.streams.filter((s) => set.has(s.from) && set.has(s.to));
    if (internal.length === 0) {
      blocks.push({ nodes: members, tears: [] });
      continue;
    }
    // sikl: tear tanlash
    let tears = internal.filter((s) => userTears.has(s.id)).map((s) => s.id);
    const entry = members.find((v) => (inlets[v] as StreamDef[]).some((s) => !set.has(s.from))) ?? (members[0] as string);
    if (tears.length === 0) {
      if (userTears.size) throw new Error(`flowsheet ${f.id}: given tear streams do not break the recycle in block [${members.join(', ')}]`);
      tears = autoTears(set, f.streams, entry);
    }
    const tearSet = new Set(tears);
    const order = topo(members, (v) => (outlets[v] as StreamDef[]).filter((s) => set.has(s.to) && !tearSet.has(s.id)).map((s) => s.to));
    if (!order) {
      if (userTears.size) throw new Error(`flowsheet ${f.id}: given tear streams do not break all recycles in block [${members.join(', ')}]`);
      throw new Error(`flowsheet ${f.id}: could not order block [${members.join(', ')}]`);
    }
    blocks.push({ nodes: order, tears });
  }
  return { blocks, inlets, outlets };
}
